import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';
import { auth, db, functions } from '../config/firebase';
import { sendEmailVerification, signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { firebaseErrorKey } from '../lib/firebaseErrors';
import { normalizeInviteCode, INVITE_CODE_MAX_LENGTH } from '../lib/inviteCode';
import { AppDownloadLinks } from './common/AppDownloadLinks';

interface SignUpFormData {
  email: string;
  password: string;
  confirmPassword: string;
  displayName: string;
  username: string;
  role: 'care_recipient' | 'caregiver';
  inviteCode?: string;
}

/** Letters, digits, dot and underscore; 3–30 characters (an optional leading @ is dropped). */
const USERNAME_PATTERN = /^[A-Za-z0-9._]{3,30}$/;

const normalizeUsername = (value: string) => value.trim().replace(/^@+/, '');

export default function SignUp() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState<SignUpFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    username: '',
    role: 'care_recipient'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  // Set once this page starts creating an account, so the "already signed in"
  // redirect below doesn't skip the success screen when the new account signs in.
  const [registering, setRegistering] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.signUp.errors.passwordMismatch'));
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.signUp.errors.passwordTooShort'));
      setLoading(false);
      return;
    }

    const username = normalizeUsername(formData.username);
    if (username && !USERNAME_PATTERN.test(username)) {
      setError(t('auth.signUp.errors.usernameInvalid'));
      setLoading(false);
      return;
    }

    const inviteCodeEntered = normalizeInviteCode(formData.inviteCode || '');
    if (formData.role === 'caregiver' && !inviteCodeEntered) {
      setError(t('auth.signUp.errors.inviteCodeRequired'));
      setLoading(false);
      return;
    }

    const email = formData.email.trim();
    const displayName = formData.displayName.trim();
    setRegistering(true);

    try {
      // Call Cloud Function to register user (onCall — must use the callable SDK)
      const registerUser = httpsCallable(functions, 'registerUser');
      const response = await registerUser({
        email,
        password: formData.password,
        displayName,
        username: username || null,
        phoneNumber: null, // No phone number for web signup
        role: formData.role,
        inviteCode: formData.role === 'caregiver' ? inviteCodeEntered : undefined,
        // Stored in Firestore and read by both mobile apps — kept in English as canonical data, not UI copy.
        businessName: formData.role === 'care_recipient' ? `${displayName}'s Care Circle` : undefined
      });

      const result = response.data as { success: boolean; uid: string; businessId: string | null };

      if (result.success) {
        // The function already created the Auth user — sign in with the new credentials
        const credential = await signInWithEmailAndPassword(auth, email, formData.password);

        // Confirms the address and doubles as the welcome email. Sent in the site's
        // language (auth.languageCode). Best effort: the account already exists.
        try {
          await sendEmailVerification(credential.user);
          setVerificationSent(true);
        } catch (verifyError) {
          console.warn('Could not send verification email:', verifyError);
        }

        // The function stores the care recipient's invite code on the user doc but doesn't return it
        let userInviteCode = '';
        if (formData.role === 'care_recipient') {
          const userDoc = await getDoc(doc(db, 'users', credential.user.uid));
          userInviteCode = userDoc.data()?.inviteCode || '';
        }

        setSuccess(true);
        setInviteCode(userInviteCode);
      } else {
        throw new Error(t('auth.signUp.errors.registrationFailed'));
      }
    } catch (err) {
      setRegistering(false);
      setError(t(firebaseErrorKey(err)));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="signup-success">
        <h2>✅ {t('auth.signUp.success.title')}</h2>
        <p>{t('auth.signUp.success.welcome', { name: formData.displayName })}</p>

        {formData.role === 'care_recipient' && inviteCode && (
          <div className="invite-code-box">
            <h3>{t('auth.signUp.success.inviteCodeTitle')}</h3>
            <div className="code">{inviteCode}</div>
            <p>{t('auth.signUp.success.inviteCodeShare')}</p>
          </div>
        )}

        {verificationSent && (
          <p className="verification-note">
            {t('auth.signUp.success.verificationSent', { email: formData.email.trim() })}
          </p>
        )}

        <Link to="/dashboard" className="btn-dashboard">
          {t('auth.signUp.success.goToDashboard')}
        </Link>

        <div className="next-steps">
          <h3>{t('auth.signUp.success.nextStepsTitle')}</h3>
          <ol>
            <li>{t('auth.signUp.success.step1')}</li>
            <li>{t('auth.signUp.success.step2')} <strong>{formData.email}</strong></li>
            <li>
              {formData.role === 'care_recipient'
                ? t('auth.signUp.success.step3CareRecipient')
                : t('auth.signUp.success.step3Caregiver')}
            </li>
          </ol>
        </div>

        <div className="download-buttons">
          <AppDownloadLinks />
        </div>
        <SignUpStyles />
      </div>
    );
  }

  if (currentUser && !registering) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="signup-container">
      <div className="signup-header">
        <h1>{t('auth.signUp.title')}</h1>
        <p>{t('auth.signUp.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="signup-form">
        {error && <div className="error-message">{error}</div>}

        {/* Role Selection */}
        <div className="form-group">
          <label>{t('auth.signUp.roleLabel')}</label>
          <div className="role-buttons">
            <button
              type="button"
              className={`role-btn ${formData.role === 'care_recipient' ? 'active' : ''}`}
              onClick={() => setFormData({ ...formData, role: 'care_recipient' })}
            >
              <span className="icon">👴</span>
              <span>{t('auth.signUp.role.careRecipient.label')}</span>
              <span className="desc">{t('auth.signUp.role.careRecipient.desc')}</span>
            </button>
            <button
              type="button"
              className={`role-btn ${formData.role === 'caregiver' ? 'active' : ''}`}
              onClick={() => setFormData({ ...formData, role: 'caregiver' })}
            >
              <span className="icon">👨‍⚕️</span>
              <span>{t('auth.signUp.role.caregiver.label')}</span>
              <span className="desc">{t('auth.signUp.role.caregiver.desc')}</span>
            </button>
          </div>
        </div>

        {/* Display Name */}
        <div className="form-group">
          <label htmlFor="displayName">{t('auth.signUp.fullName')} *</label>
          <input
            id="displayName"
            type="text"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            placeholder={t('auth.signUp.fullNamePlaceholder')}
            required
          />
        </div>

        {/* Username (optional) */}
        <div className="form-group">
          <label htmlFor="username">{t('auth.signUp.username')}</label>
          <input
            id="username"
            type="text"
            autoCapitalize="none"
            autoComplete="username"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            placeholder={t('auth.signUp.usernamePlaceholder')}
          />
          {formData.username && (
            <small>{t('auth.signUp.usernameHint', { username: normalizeUsername(formData.username) })}</small>
          )}
        </div>

        {/* Email */}
        <div className="form-group">
          <label htmlFor="email">{t('auth.signUp.email')} *</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder={t('auth.signUp.emailPlaceholder')}
            required
          />
        </div>

        {/* Password */}
        <div className="form-group">
          <label htmlFor="password">{t('auth.signUp.password')} *</label>
          <input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder={t('auth.signUp.passwordPlaceholder')}
            required
            minLength={6}
          />
        </div>

        {/* Confirm Password */}
        <div className="form-group">
          <label htmlFor="confirmPassword">{t('auth.signUp.confirmPassword')} *</label>
          <input
            id="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            placeholder={t('auth.signUp.confirmPasswordPlaceholder')}
            required
          />
        </div>

        {/* Invite Code (for caregivers only) */}
        {formData.role === 'caregiver' && (
          <div className="form-group">
            <label htmlFor="inviteCode">{t('auth.signUp.inviteCode')} *</label>
            <input
              id="inviteCode"
              type="text"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              maxLength={INVITE_CODE_MAX_LENGTH + 2}
              value={formData.inviteCode || ''}
              onChange={(e) => setFormData({ ...formData, inviteCode: e.target.value.toUpperCase() })}
              placeholder={t('auth.signUp.inviteCodePlaceholder')}
              required
            />
            <small>{t('auth.signUp.inviteCodeHint')}</small>
          </div>
        )}

        <button type="submit" className="btn-submit" disabled={loading}>
          {loading ? t('auth.signUp.submitting') : t('auth.signUp.submit')}
        </button>

        <p className="signin-link">
          {t('auth.signUp.haveAccount')} <Link to="/signin">{t('auth.signUp.signInLink')}</Link>
        </p>
      </form>

      <SignUpStyles />
    </div>
  );
}

/** Styles for both the form and the success screen. */
function SignUpStyles() {
  return (
    <style>{`
        .signup-container {
          max-width: 500px;
          margin: 40px auto;
          padding: 32px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .signup-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .signup-header h1 {
          font-size: 32px;
          color: #1a1a1a;
          margin-bottom: 8px;
        }

        .signup-header p {
          color: #666;
          font-size: 16px;
        }

        .signup-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          font-weight: 600;
          color: #333;
          font-size: 14px;
        }

        .form-group input {
          padding: 12px 16px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.2s;
        }

        .form-group input:focus {
          outline: none;
          border-color: #4285f4;
        }

        .form-group small {
          color: #666;
          font-size: 13px;
        }

        .role-buttons {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .role-btn {
          padding: 20px;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          background: white;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }

        .role-btn:hover {
          border-color: #4285f4;
        }

        .role-btn.active {
          border-color: #4285f4;
          background: #e3f2fd;
        }

        .role-btn .icon {
          font-size: 40px;
        }

        .role-btn .desc {
          font-size: 12px;
          color: #666;
          text-align: center;
        }

        .btn-submit {
          padding: 14px;
          background: #4285f4;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-submit:hover:not(:disabled) {
          background: #357ae8;
        }

        .btn-submit:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .error-message {
          padding: 12px;
          background: #ffebee;
          color: #c62828;
          border-radius: 8px;
          font-size: 14px;
        }

        .signin-link {
          text-align: center;
          color: #666;
          font-size: 14px;
        }

        .signin-link a {
          color: #4285f4;
          text-decoration: none;
          font-weight: 600;
        }

        .signup-success {
          max-width: 600px;
          margin: 40px auto;
          padding: 40px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          text-align: center;
        }

        .signup-success h2 {
          color: #2e7d32;
          margin-bottom: 16px;
        }

        .invite-code-box {
          margin: 32px 0;
          padding: 24px;
          background: #e3f2fd;
          border-radius: 12px;
        }

        .invite-code-box .code {
          font-size: 36px;
          font-weight: bold;
          color: #1565c0;
          letter-spacing: 4px;
          margin: 16px 0;
          font-family: monospace;
        }

        .next-steps {
          text-align: left;
          margin: 32px 0;
          padding: 24px;
          background: #f5f5f5;
          border-radius: 12px;
        }

        .next-steps ol {
          margin: 16px 0;
          padding-left: 24px;
        }

        .next-steps li {
          margin: 12px 0;
          line-height: 1.6;
        }

        .download-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          margin-top: 32px;
        }

        .btn-download {
          padding: 14px 28px;
          background: #333;
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 600;
          transition: background 0.2s;
        }

        .btn-download:hover {
          background: #555;
        }

        .verification-note {
          margin: 16px 0;
          padding: 12px 16px;
          background: #e8f5e9;
          color: #2e7d32;
          border-radius: 8px;
        }

        .btn-dashboard {
          display: inline-block;
          margin-top: 8px;
          padding: 14px 28px;
          background: #4285f4;
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 600;
        }

        .btn-dashboard:hover {
          background: #357ae8;
        }

        .download-buttons .app-download {
          align-items: center;
        }

        @media (max-width: 640px) {
          .signup-container,
          .signup-success {
            margin: 24px 16px;
            padding: 24px 20px;
          }

          .signup-header h1 {
            font-size: 26px;
          }

          .role-buttons {
            grid-template-columns: 1fr;
          }

          .role-btn {
            flex-direction: row;
            padding: 14px 16px;
            text-align: left;
          }

          .role-btn .icon {
            font-size: 32px;
          }

          .role-btn .desc {
            text-align: left;
          }

          .invite-code-box .code {
            font-size: 28px;
          }

          .next-steps {
            padding: 16px;
          }
        }
    `}</style>
  );
}
